/**
 * Facebook / Meta Pixel adapter.
 * Maps domain `trackEvent` names onto Meta events and dispatches via the
 * existing pixel + CAPI helpers. Unmapped events are ignored.
 */

import { warnOnce } from './logger';
import { FACEBOOK_EVENT_MAP } from '../../constants';
import type { FacebookEventDTO } from './facebook.dto';
import type { EventTracker } from '../../domain/interfaces/EventTracker';
import { getUserInfo } from '../../utils/commonAttributes';
import {
  commonFacebookEventsUtil,
  normalizePhoneForFacebookEvents,
  toFacebookStringArray,
} from '../../utils/facebookCommonAttributes';
import {
  commonFacebookEventsUtilIfMetaPixelFires,
  commonFacebookEventsUtilWithServerAttribution,
} from '../../utils/facebookServerEventAttribution';

export interface FacebookAdapterConfig {
  enabled?: boolean;
}

const FACEBOOK_PDP_ATTRIBUTE_KEYS = [
  'content_ids',
  'content_type',
  'content_names',
  'content_category',
  'contents',
  'value',
  'currency',
  'design_id',
  'variant_id',
  'collection_name',
  'metal_type',
  'metal_colour',
  'purity',
  'stone_type',
  'diamond_quality',
  'size',
  'solitaire_type',
  'solitaire_id',
  'solitaire_shape',
  'solitaire_carat',
  'solitaire_colour',
  'solitaire_clarity',
  'solitaire_cut_grade',
] as const;

const FACEBOOK_EVENT_ATTRIBUTE_KEYS: Record<string, readonly string[]> = {
  PageView: ['CONNECTO_ID'],
  ViewContent: FACEBOOK_PDP_ATTRIBUTE_KEYS,
  AddToCart: FACEBOOK_PDP_ATTRIBUTE_KEYS,
  CustomizeProduct: FACEBOOK_PDP_ATTRIBUTE_KEYS,
  'PDP Video Played': FACEBOOK_PDP_ATTRIBUTE_KEYS,
  AddToWishlist: [...FACEBOOK_PDP_ATTRIBUTE_KEYS, 'wishlist_item_count_after'],
  'PLP Viewed': [
    'content_ids',
    'content_type',
    'content_names',
    'content_category',
    'result_count',
    'list_name',
    'sort_order',
  ],
  Search: [
    'content_type',
    'content_ids',
    'search_source',
    'result_count',
    'search_string',
    'sort_order',
    'surface_title',
  ],
  CompleteRegistration: [
    'registration_method',
    'registration_context',
    'customer_status',
    'em',
    'ph',
    'external_id',
    'fn',
    'zp',
    'st',
    'ct',
  ],
  FindLocation: [
    'content_names',
    'content_category',
    'store_count',
    'store_ids',
    'store_names',
    'store_city',
    'city',
    'intent_source',
  ],
  DeliveryAvailabilityChecked: [
    'content_names',
    'content_category',
    'content_ids',
    'content_type',
    'broad_city',
    'nearby_store_available',
    'value',
    'currency',
  ],
  StoreInventoryViewed: [
    'store_id',
    'store_name',
    'city',
    'content_category',
    'content_ids',
    'result_count',
    'source_page',
  ],
  RecentlyViewedOpened: [
    'content_ids',
    'content_names',
    'content_category',
    'content_type',
    'num_items',
    'currency',
    'source_page',
  ],
};

function pickDefinedKeys(
  properties: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const key of keys) {
    if (properties[key] !== undefined) {
      next[key] = properties[key];
    }
  }
  return next;
}

/** Meta's `value` param must always be numeric (int/float) — never falls back to a string. */
function toMetaValue(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : undefined;
}

function hasFacebookPdpShape(properties: Record<string, unknown>): boolean {
  return Array.isArray(properties.content_ids) || typeof properties.content_ids === 'string';
}

function getWishlistItemCountAfter(): number | undefined {
  const userInfo = getUserInfo();
  return userInfo?.wishlistCount != null ? Number(userInfo.wishlistCount) : undefined;
}

function listingContentIds(properties: Record<string, unknown>): string[] {
  const fromIds = toFacebookStringArray(properties.content_ids);
  if (fromIds.length) return fromIds;
  const productId = properties['Product Id'] ?? properties.design_id;
  return productId != null && productId !== '' ? [String(productId)] : [];
}

function listingContentNames(properties: Record<string, unknown>): string[] {
  const fromNames = toFacebookStringArray(properties.content_names);
  if (fromNames.length) return fromNames;
  const productName = properties['Product Name'];
  return productName ? [String(productName)] : [];
}

/**
 * Build the exact Meta custom-data object for an event. Never forwards
 * WebEngage keys (`Product Id`, `collection_id`, `Pincode`, …).
 */
function toFacebookAttributes(
  properties: Record<string, unknown>,
  metaEventName: string,
): Record<string, unknown> {
  const allowedKeys = FACEBOOK_EVENT_ATTRIBUTE_KEYS[metaEventName];
  if (!allowedKeys) return {};

  if (metaEventName === 'PageView') {
    return pickDefinedKeys(properties, allowedKeys);
  }

  if (metaEventName === 'StoreInventoryViewed') {
    return pickDefinedKeys(
      {
        store_id: properties.store_id,
        store_name: properties.store_name,
        city: properties.city,
        content_category: '',
        content_ids: [],
        result_count:
          properties.result_count != null ? String(properties.result_count) : undefined,
        source_page: properties.source_page ?? 'pdp',
      },
      allowedKeys,
    );
  }

  if (metaEventName === 'CompleteRegistration') {
    const email = String(properties.email ?? properties.em ?? '').trim().toLowerCase();
    const phone = normalizePhoneForFacebookEvents(properties.phone ?? properties.ph);
    const userCode = String(properties.userCode ?? properties.external_id ?? '').trim();
    const firstName = String(
      properties.firstName ?? properties.fn ?? properties.name ?? '',
    ).trim();
    const postcode = String(
      properties.postcode ?? properties.pincode ?? properties.zp ?? '',
    ).trim();
    const state = String(properties.state ?? properties.st ?? '').trim();
    const city = String(properties.city ?? properties.ct ?? '').trim();
    return pickDefinedKeys(
      {
        registration_method: properties.registration_method,
        registration_context: properties.registration_context ?? 'other',
        customer_status: properties.customer_status,
        ...(email ? { em: email } : {}),
        ...(phone ? { ph: phone } : {}),
        ...(userCode ? { external_id: userCode } : {}),
        ...(firstName ? { fn: firstName } : {}),
        ...(postcode ? { zp: postcode } : {}),
        ...(state ? { st: state } : {}),
        ...(city ? { ct: city } : {}),
      },
      allowedKeys,
    );
  }

  if (metaEventName === 'DeliveryAvailabilityChecked') {
    const contentIds = listingContentIds(properties);
    const contentNames = listingContentNames(properties);
    return pickDefinedKeys(
      {
        content_names: contentNames,
        content_category: properties.content_category ?? properties['Category Name'] ?? '',
        content_ids: contentIds,
        content_type: 'product',
        broad_city: properties.broad_city ?? '',
        nearby_store_available: properties.nearby_store_available ?? '',
        value: 0,
        currency: 'INR',
      },
      allowedKeys,
    );
  }

  if (hasFacebookPdpShape(properties)) {
    const picked = pickDefinedKeys(properties, allowedKeys);
    if (metaEventName === 'AddToWishlist' && picked.wishlist_item_count_after == null) {
      picked.wishlist_item_count_after = getWishlistItemCountAfter();
    }
    return picked;
  }

  if (metaEventName === 'AddToWishlist') {
    const productId = properties['Product Id'] ?? properties.design_id;
    const contentId = productId != null && productId !== '' ? String(productId) : undefined;
    const sellingPrice = toMetaValue(properties['Selling Price'] ?? properties.value);
    const variantId = properties['Customization Id'] ?? properties.variant_id ?? contentId;
    const contents = contentId
      ? [
          {
            id: contentId,
            quantity: 1,
            ...(sellingPrice != null ? { item_price: sellingPrice } : {}),
          },
        ]
      : [];

    return pickDefinedKeys(
      {
        content_names: listingContentNames(properties),
        content_category: properties['Category Name'] ?? '',
        content_ids: contentId ? [contentId] : [],
        content_type: 'product',
        contents,
        value: sellingPrice ?? 0,
        currency: 'INR',
        design_id: contentId,
        variant_id: variantId != null && variantId !== '' ? String(variantId) : contentId,
        wishlist_item_count_after: getWishlistItemCountAfter(),
      },
      allowedKeys,
    );
  }

  return pickDefinedKeys(properties, allowedKeys);
}

export class FacebookAdapter implements EventTracker {
  private config: FacebookAdapterConfig;
  private initialized = false;

  constructor(config: FacebookAdapterConfig = {}) {
    this.config = {
      enabled: true,
      ...config,
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.config.enabled) return;
    this.initialized = true;
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.config.enabled) {
      warnOnce('fb:disabled', 'Facebook adapter is disabled');
      return;
    }

    const mapping = FACEBOOK_EVENT_MAP[eventName];
    if (!mapping) return;

    // TEMPORARY: `event_id` is read straight off the raw properties (not the allow-listed
    // `toFacebookAttributes` output) so `trackPageView.ts` can pre-generate one id that
    // WebEngage's `PageView` event also carries.
    const rawEventId = properties?.event_id;
    const eventId =
      typeof rawEventId === 'string' && rawEventId.trim() ? rawEventId.trim() : undefined;

    const dto: FacebookEventDTO = {
      eventName: mapping.metaName,
      attributes: toFacebookAttributes(properties ?? {}, mapping.metaName),
      dispatchStyle: mapping.dispatchStyle ?? 'default',
      eventId,
    };

    this.dispatch(dto);
  }

  private dispatch(dto: FacebookEventDTO): void {
    if (dto.dispatchStyle === 'serverAttribution') {
      commonFacebookEventsUtilWithServerAttribution(dto.eventName, dto.attributes);
      return;
    }
    if (dto.dispatchStyle === 'ifPixelFires') {
      commonFacebookEventsUtilIfMetaPixelFires(dto.eventName, dto.attributes);
      return;
    }
    commonFacebookEventsUtil(dto.eventName, dto.attributes, undefined, dto.eventId);
  }
}
